import { useMemo, useState } from "react";
import { toId } from "../../utils/examsschedule/examHelpers";

const studentLabel = (s) => {
  if (!s) return "";
  const name =
    s.name ||
    [s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ") ||
    s.admissionNo ||
    "Student";
  const roll = s.rollNumber || s.admissionNo;
  return roll ? `${roll}. ${name}` : name;
};

/**
 * Bench × seat grid for one room.
 * seatsPerBench = 1 or 2 (from ExamRoom master).
 * Emits seatAllocations: [{ benchNo, seatIndex, studentId, sectionId, classId, seatNumber }]
 */
export default function SeatingGrid({
  totalBenches = 0,
  seatsPerBench = 1,
  seatAllocations = [],
  studentOptions = [],
  classId = "",
  disabled = false,
  onChange,
}) {
  const benches = Math.max(0, Number(totalBenches) || 0);
  const perBench = Number(seatsPerBench) === 2 ? 2 : 1;

  const [picking, setPicking] = useState(null); // { benchNo, seatIndex }

  const byCell = useMemo(() => {
    const map = new Map();
    for (const a of seatAllocations || []) {
      const key = `${a.benchNo}-${a.seatIndex || 1}`;
      map.set(key, a);
    }
    return map;
  }, [seatAllocations]);

  const seatedIds = useMemo(() => {
    const set = new Set();
    for (const a of seatAllocations || []) {
      const id = toId(a.studentId);
      if (id) set.add(id);
    }
    return set;
  }, [seatAllocations]);

  const availableStudents = useMemo(
    () => studentOptions.filter((s) => !seatedIds.has(toId(s))),
    [studentOptions, seatedIds]
  );

  const studentById = useMemo(() => {
    const m = new Map();
    for (const s of studentOptions) m.set(toId(s), s);
    return m;
  }, [studentOptions]);

  const upsertSeat = (benchNo, seatIndex, student) => {
    if (disabled) return;
    const next = (seatAllocations || []).filter(
      (a) => !(Number(a.benchNo) === benchNo && Number(a.seatIndex || 1) === seatIndex)
    );
    if (student) {
      const sid = toId(student);
      // Remove student from any other seat in this room
      const withoutStudent = next.filter((a) => toId(a.studentId) !== sid);
      withoutStudent.push({
        benchNo,
        seatIndex,
        studentId: sid,
        classId: toId(student.classId) || classId || "",
        sectionId: toId(student.sectionId) || "",
        seatNumber: `${benchNo}${perBench === 2 ? (seatIndex === 1 ? "A" : "B") : ""}`,
      });
      onChange?.(withoutStudent);
    } else {
      onChange?.(next);
    }
    setPicking(null);
  };

  const clearSeat = (benchNo, seatIndex) => {
    upsertSeat(benchNo, seatIndex, null);
  };

  const autoFill = () => {
    if (disabled || availableStudents.length === 0 || benches === 0) return;
    const next = [...(seatAllocations || [])];
    const taken = new Set(seatedIds);
    let si = 0;
    for (let b = 1; b <= benches && si < availableStudents.length; b++) {
      for (let seat = 1; seat <= perBench && si < availableStudents.length; seat++) {
        const key = `${b}-${seat}`;
        if (byCell.has(key)) continue;
        const student = availableStudents[si++];
        const sid = toId(student);
        if (taken.has(sid)) continue;
        taken.add(sid);
        next.push({
          benchNo: b,
          seatIndex: seat,
          studentId: sid,
          classId: toId(student.classId) || classId || "",
          sectionId: toId(student.sectionId) || "",
          seatNumber: `${b}${perBench === 2 ? (seat === 1 ? "A" : "B") : ""}`,
        });
      }
    }
    onChange?.(next);
  };

  const clearAll = () => {
    if (disabled) return;
    onChange?.([]);
    setPicking(null);
  };

  if (benches < 1) {
    return (
      <p className="text-xs text-gray-400 dark:text-neutral-500">
        Select a room with benches to open the seating grid
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-500 dark:text-neutral-400">
          Grid: {benches} bench(es) × {perBench} seat(s) ·{" "}
          {(seatAllocations || []).length} seated · {availableStudents.length}{" "}
          free
        </div>
        {!disabled && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={autoFill}
              className="text-xs text-indigo-600 hover:underline"
            >
              Auto-fill sequential
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="text-xs text-red-600 hover:underline"
            >
              Clear seats
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <div
          className="inline-grid gap-2 p-2 bg-gray-50 dark:bg-neutral-950 border rounded-xl"
          style={{
            gridTemplateColumns: `repeat(${perBench}, minmax(7.5rem, 1fr))`,
          }}
        >
          {Array.from({ length: benches }, (_, bi) => {
            const benchNo = bi + 1;
            return Array.from({ length: perBench }, (_, si) => {
              const seatIndex = si + 1;
              const key = `${benchNo}-${seatIndex}`;
              const alloc = byCell.get(key);
              const student = alloc
                ? studentById.get(toId(alloc.studentId))
                : null;
              const isPicking =
                picking &&
                picking.benchNo === benchNo &&
                picking.seatIndex === seatIndex;

              return (
                <div
                  key={key}
                  className={`relative min-h-[4.5rem] rounded-lg border p-2 text-left ${ alloc ? "bg-indigo-50 border-indigo-300" : "bg-white dark:bg-neutral-900 border-dashed border-gray-300 dark:border-neutral-700" }`}
                >
                  <div className="text-[10px] font-medium text-gray-400 dark:text-neutral-500 mb-1">
                    Bench {benchNo}
                    {perBench === 2 ? (seatIndex === 1 ? " · A" : " · B") : ""}
                  </div>

                  {alloc ? (
                    <div>
                      <div className="text-xs font-medium text-gray-800 dark:text-neutral-100 leading-tight">
                        {studentLabel(student) || "Assigned"}
                      </div>
                      {student?.sectionId?.sectionName && (
                        <div className="text-[10px] text-gray-400 dark:text-neutral-500">
                          {student.sectionId.sectionName}
                        </div>
                      )}
                      {!disabled && (
                        <button
                          type="button"
                          className="text-[10px] text-red-600 mt-1 hover:underline"
                          onClick={() => clearSeat(benchNo, seatIndex)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ) : isPicking ? (
                    <select
                      autoFocus
                      className="w-full border rounded text-xs px-1 py-1"
                      defaultValue=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (!id) {
                          setPicking(null);
                          return;
                        }
                        const st = studentOptions.find((s) => toId(s) === id);
                        upsertSeat(benchNo, seatIndex, st);
                      }}
                      onBlur={() => setPicking(null)}
                    >
                      <option value="">Pick student…</option>
                      {availableStudents.map((s) => (
                        <option key={toId(s)} value={toId(s)}>
                          {studentLabel(s)}
                          {s.sectionId?.sectionName
                            ? ` (${s.sectionId.sectionName})`
                            : ""}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <button
                      type="button"
                      disabled={disabled}
                      className="text-[11px] text-gray-400 dark:text-neutral-500 hover:text-indigo-600 disabled:opacity-40"
                      onClick={() => setPicking({ benchNo, seatIndex })}
                    >
                      + Seat student
                    </button>
                  )}
                </div>
              );
            });
          }).flat()}
        </div>
      </div>
    </div>
  );
}
